import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import CreateApi from './Settings/appSettings';
import { useMCContext } from './contexts/useMCStates';
import { LeaderboardDisplay } from '@moment-golf/leaderboard';
import type { SeriesPoints } from '@moment-golf/leaderboard';

interface LeaderboardViewProps {
    /** Full event object. If null/undefined the component shows a placeholder. */
    event: any;
    headless?: boolean;
    hideSelector?: boolean;
}

export const LeaderboardView: React.FC<LeaderboardViewProps> = ({
    event,
    headless = true,
    hideSelector = true,
}) => {
    const api = CreateApi();
    const { factor, appTheme, windowSize } = useMCContext();

    const [leaderboardData, setLeaderboardData] = useState<any[]>([]);
    const [courseData, setCourseData] = useState<any[]>([]);
    const [eventInfo, setEventInfo] = useState<any>(null);
    const [lastQuery, setLastQuery] = useState<Date>(new Date('2000-01-01'));
    const [seriesPoints, setSeriesPoints] = useState<SeriesPoints | undefined>(undefined);

    // Reset and fetch fresh whenever the event changes
    useEffect(() => {
        if (!event?._id) return;
        setLeaderboardData([]);
        setCourseData([]);
        setEventInfo(null);
        setLastQuery(new Date('2000-01-01'));
        setSeriesPoints(undefined);
        fetchData(new Date('2000-01-01'));

        const interval = setInterval(() => fetchData(), 60000);
        return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [event?._id]);

    // Resolve series points from the event's collection.
    // eventGroupIDs may be on the prop directly or arrive via eventInfo from the lb API.
    useEffect(() => {
        const eventGroupIDs: string[] =
            event?.eventGroupIDs ?? eventInfo?.eventGroupIDs ?? [];
        if (eventGroupIDs.length === 0) {
            setSeriesPoints(undefined);
            return;
        }
        api.get('/lb/GetGroupSeriesPoints', { params: { _id: eventGroupIDs[0] } })
            .then((res: any) => setSeriesPoints(res.data?.seriesPoints ?? undefined))
            .catch(() => setSeriesPoints(undefined));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [event?._id, eventInfo?.eventGroupIDs]);

    const fetchData = async (refDateOverride?: Date) => {
        if (!event?._id) return;
        try {
            const response = await api.get('/lb/GetLeaderboardData', {
                params: {
                    eventID: event._id,
                    refDate: refDateOverride ?? lastQuery,
                },
            });
            const data = response.data;

            setLeaderboardData((prev) => {
                if (prev.length === 0) return data.eventScores ?? [];
                // Delta-merge: update changed entries, keep unchanged
                return prev.map((item: any) => {
                    const match = data.eventScores?.find((s: any) => s._id === item._id);
                    return match ?? item;
                });
            });

            if (data.leaderBoardCourseData?.length > 0) {
                setCourseData(data.leaderBoardCourseData);
            }
            if (data.eventInfo) {
                setEventInfo(data.eventInfo);
            }
            setLastQuery(new Date());
        } catch (err) {
            console.error('LeaderboardView: fetch error', err);
        }
    };

    if (!event?._id) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                    minHeight: 120,
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    Select an event to view standings
                </Typography>
            </Box>
        );
    }

    // Merge event with eventInfo from API so LeaderboardDisplay has rounds, scoreFormat, etc. for round selector and gross/net toggle
    const eventData = eventInfo ? { ...event, ...eventInfo } : event;

    return (
        <LeaderboardDisplay
            leaderboardData={leaderboardData}
            courseData={courseData}
            eventData={eventData}
            currentRound={1}
            scoreCode=""
            factor={factor}
            appTheme={appTheme}
            windowSize={{ appBarHeight: 0, ...windowSize }}
            isPWA={false}
        headless={headless}
        hideSelector={hideSelector}
        embedded={true}
        seriesPoints={seriesPoints}
    />
    );
};
